// src/app/student-profile-summary/student-profile-summary.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsOffcanvasComponent } from '../../settings-offcanvas/settings-offcanvas.component';
import { Subscription } from 'rxjs';


import { AuthService } from '../../core/services/auth.service';
import { SchoolService } from '../../core/services/school.service';

import {
  ApplicationData,
  StudentData,
  GetApplicationStudentsResponse 
} from '../../core/models/auth.model'; 

@Component({
  selector: 'app-student-profile-summary',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SettingsOffcanvasComponent
  ],
  templateUrl: './student-profile-summary.component.html',
  styleUrls: ['./student-profile-summary.component.css']
})
export class StudentProfileSummaryComponent implements OnInit, OnDestroy {
  students: StudentData[] = [];
  isLoading = false; 
  errorMessage: string | null = null; 
  private applicationsSubscription: Subscription | undefined;
  private studentsRequestSubscription: Subscription | undefined;

  constructor(
    private router: Router,
    private translate: TranslateService, 
    private schoolService: SchoolService, 
    private authService: AuthService 
  ) {

  }

  ngOnInit(): void {
    this.loadStudentsForFirstApplication();
  }

  ngOnDestroy(): void {
    if (this.applicationsSubscription) {
      this.applicationsSubscription.unsubscribe();
    }
    if (this.studentsRequestSubscription) {
      this.studentsRequestSubscription.unsubscribe();
    }
  }

  public getAlphabeticalIndex(index: number): string {
    if (index < 0) return '';
    return String.fromCharCode(65 + index); 
  }

  public getCurrentStudentName(student: StudentData): string {
    const currentLang = this.translate.currentLang;
    if (currentLang === 'en') {
      if (student.eng_name) {
        return student.eng_name;
      } else if (student.chi_name) {
        return student.chi_name;
      }
    } else if (currentLang === 'zh-CN' || currentLang === 'zh-HK') {
      if (student.chi_name) {
        return student.chi_name;
      } else if (student.eng_name) {
        return student.eng_name;
      }
    }else{
      return student.chi_name;
    }
    return 'SUMMARY.ANONYMOUS_STUDENT';
  }

  loadStudentsForFirstApplication(): void {
    this.isLoading = true;
    this.errorMessage = null; 
    this.students = []; 
    this.applicationsSubscription = this.authService.activeApplications$.subscribe({
      next: (applications: ApplicationData[] | null) => {
        if (applications && applications.length > 0) {
          const firstApplicationId = applications[0].id;
          console.log(`application (ID: ${firstApplicationId}).`);
          this.studentsRequestSubscription = this.schoolService.getApplicationStudents(firstApplicationId).subscribe({
            next: (response: GetApplicationStudentsResponse) => {
              this.isLoading = false; 
              if (response.status === 'ok' && response.data && response.data.students) {
                this.students = response.data.students;
                console.log(`Loaded students for application ID ${firstApplicationId}:`, this.students);
                if (this.students.length === 0) {
                  this.errorMessage = 'STUDENT_SUMMARY.NO_STUDENTS_FOR_APPLICATION';
                }
              } else {
                this.students = []; 
                this.errorMessage = this.getStudentErrorMessageKey(response);
                console.error(`Failed to load students for application ID ${firstApplicationId}:`, response.message || response.errors);
              }
            },
            error: (err) => {
              this.isLoading = false; 
              this.students = [];
              this.errorMessage = 'STUDENT_SUMMARY.ERROR_FETCHING_STUDENTS';
              console.error(`HTTP error fetching students for application ID ${firstApplicationId}:`, err);
            }
          });
        } else {
          this.isLoading = false; 
          this.students = []; 
          this.errorMessage = 'APPLICATION_SUMMARY.NO_APPLICATIONS_FOR_ACTIVE_TERM';
          console.warn('No active term application data found in AuthService; cannot load students.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.students = [];
        this.errorMessage = 'STUDENT_SUMMARY.ERROR_GETTING_APPLICATIONS_FROM_AUTHSERVICE';
        console.error('Error retrieving active application list from AuthService:', err);
      }
    });
  }

  goToAddStudentPage(): void {
    this.router.navigate(['/add-student-profile']);
  }

  viewStudentDetails(studentId: number, studentDisplayName: string): void {
    console.log(`Attempting to view details for student "${studentDisplayName}" (ID: ${studentId}).`);
    alert(`Next step: Display details for student ID ${studentId} (Functionality to be implemented).`);
  }

  private getStudentErrorMessageKey(response: any): string {
    if (response.message) {
      return response.message;
    }
    if (response.error && typeof response.error === 'string') {
      return response.error;
    }
    if (response.errors) {
      const errorMessagesArray: string[] = [];
      Object.keys(response.errors).forEach(key => {
        const messages = response.errors[key];
        if (messages && Array.isArray(messages)) {
          errorMessagesArray.push(...messages);
        } else if (typeof messages === 'string') {
          errorMessagesArray.push(messages);
        }
      });
      if (errorMessagesArray.length > 0) {
        return 'STUDENT_SUMMARY.ERROR_LOADING_STUDENTS_VALIDATION_DETAILS' + ': ' + errorMessagesArray.join(' ');
      }
    }
    return 'STUDENT_SUMMARY.ERROR_LOADING_STUDENTS';
  }

  private extractErrorMessage(response: any): string | null {
    if (!response) return null;
    if (response.message) return response.message;
    if (response.error && typeof response.error === 'string') return response.error;
    if (response.errors) {
      const errorMessagesArray: string[] = [];
      Object.keys(response.errors).forEach(key => {
        const messages = response.errors[key];
        if (messages && Array.isArray(messages)) {
          errorMessagesArray.push(...messages);
        } else if (typeof messages === 'string') {
          errorMessagesArray.push(messages);
        }
      });
      if (errorMessagesArray.length > 0) {
        return errorMessagesArray.join(' ');
      }
    }
    return null;
  }
}